import { defineComponent as _defineComponent } from 'vue'
import { t } from '@/plugins/i18n';
import { reactive, ref, onMounted, onUnmounted, ComputedRef, computed, Ref } from 'vue';
import { useNewFloorStore } from '@/stores'
import { Collection } from '@eencloud/eewc-components/src/components/text-inputs/searchBar/types';
import { debounce } from '@/service/helpers'
import {
  PlacesService,
  createMap,
  AutocompleteService
} from '@/service/googleMaps/googleMaps'
// @ts-ignore
import google from '@types/google.maps';
import { useAppStore } from '@/stores/app'
import DraggableResizableRotatable from '@/components/DraggableResizableRotatable.vue'

interface googleData {
  google: google,
  map: google.maps.map,
  placesService: google.maps.places.PlacesService,
  autocompleteService: google.maps.places.AutocompleteService,
}
type Result = {
  name: string,
  id: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SetLocation',
  props: {
    stepIndex: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  stepIndex: number
};

const newFloorStore = useNewFloorStore();
const mapToggle = ref(false);

const currentSearch = ref<string>('');
const appStore = useAppStore();

const mapNode: Ref<HTMLElement | null> = ref(null);
const data: googleData = reactive({
  google: null,
  map: null,
  placesService: null,
  autocompleteService: null
});
const foundedResults: Ref<Result[] | []> = ref([]);
const searchCollection: ComputedRef<Collection[]> = computed(() => {
  return [
    {
      items: foundedResults.value,
      callback: async function(value) {
        appStore.loading = true;
        try {
          const response = await data.placesService.searchPlaceFromQuery(value.name);
          data.map.setCenter(response);
          data.map.setZoom(17);
        } catch {
          // empty
        }
        appStore.loading = false;
      }
    }
  ]
})

onMounted(() => {
  newFloorStore.setIsCompletedStep(props.stepIndex, true);
  initMap();
});
expose({
  calculateFloorplanCoords
});

function setUserPosition() {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(userPosition => {
      data.map.setCenter({
        lat: userPosition.coords.latitude,
        lng: userPosition.coords.longitude
      });
      data.map.setZoom(17);
    });
  }
}

async function trySetAddressCoords() {
  const address = newFloorStore?.selectedLocation?.address;
  const formattedAddress = `${address?.streetAddress} ${address?.streetAddress2} ${address?.postalCode} ${address?.city}`;
  const addressWithoutSpaces = formattedAddress.replace(/\s+/g, ' ').trim();
  appStore.loading = true;

  if (addressWithoutSpaces && data.google.maps) {
    try {
      const response: google.maps.LatLng = await data.placesService.searchPlaceFromQuery(addressWithoutSpaces);
      currentSearch.value = addressWithoutSpaces;
      data.map.setCenter(response);
      data.map.setZoom(17);
    } catch {
      await setUserPosition();
    }
  } else {
    await setUserPosition();
  }

  appStore.loading = false;
}

const currentSearchChanged = debounce(async function(currentSearchValue: string){
  appStore.loading = true

  if (currentSearchValue.length === 0) {
    appStore.loading = false;
    return;
  }

  try {
    const response = await data.autocompleteService.getPlacePredictionsFromGoogle(currentSearchValue);
    foundedResults.value = response;
  } catch {
    // empty
  }

  appStore.loading = false;
}, 600);

async function initMap() {
  try {
    const zoomIn = document.getElementById("map--zoom-in");
    const zoomOut = document.getElementById("map--zoom-out");
    const overview = document.getElementById("overview");

    const mapInstance = await createMap(
      mapNode.value as HTMLElement,
      {
        zoomIn: zoomIn,
        zoomOut: zoomOut,
        overview: overview,
        mapToggle: mapToggle
      }
    );
    data.map = mapInstance.map;
    data.google = mapInstance.google;
    data.placesService = new PlacesService(data.google);
    data.autocompleteService = new AutocompleteService(data.google);
    await trySetAddressCoords();
  } catch (error) {
    console.error(error);
  }
}

function point2LatLng(point: { x: number, y: number }) {
  const topRight = data.map.getProjection().fromLatLngToPoint(data.map.getBounds().getNorthEast());
  const bottomLeft = data.map.getProjection().fromLatLngToPoint(data.map.getBounds().getSouthWest());
  const scale = Math.pow(2, data.map.getZoom());
  const worldPoint = new data.google.maps.Point(point.x / scale + bottomLeft.x, point.y / scale + topRight.y);
  return data.map.getProjection().fromPointToLatLng(worldPoint).toJSON();
}

function getRelativePos() {
  const parentPos: DOMRect = document.getElementsByClassName('map-page')[0].getBoundingClientRect();
  const floorplanTopLeftPointRectPos: DOMRect = document.getElementsByClassName('resize-handler tl')[0].getBoundingClientRect();
  const floorplanBottomRightPointRectPos: DOMRect = document.getElementsByClassName('resize-handler br')[0].getBoundingClientRect();
  return {
    topLeftRelativePos:
      {
        x: floorplanTopLeftPointRectPos.x - parentPos.x,
        y: floorplanTopLeftPointRectPos.y - parentPos.y
      },
    bottomRightRelativePos:
      {
        x: floorplanBottomRightPointRectPos.x - parentPos.x,
        y: floorplanBottomRightPointRectPos.y - parentPos.y
      }
  }
}
function calculateFloorplanCoords() {
  const floorplanRelativePos = getRelativePos();
  const topLeftCoords = point2LatLng(floorplanRelativePos.topLeftRelativePos);
  const bottomRightCoords = point2LatLng(floorplanRelativePos.bottomRightRelativePos);
  newFloorStore.$patch({
    topLeftCoordinates: { latitude: topLeftCoords.lat, longitude: topLeftCoords.lng },
    bottomRightCoordinates: { latitude: bottomRightCoords.lat, longitude: bottomRightCoords.lng }
  })
}

onUnmounted(() => {
  const containers = document.querySelectorAll('.pac-container');

  containers.forEach(container => {
    container.remove();
  });
})


return { __sfc: true,newFloorStore, mapToggle, props, currentSearch, appStore, mapNode, data, foundedResults, searchCollection, setUserPosition, trySetAddressCoords, currentSearchChanged, initMap, point2LatLng, getRelativePos, calculateFloorplanCoords, t, DraggableResizableRotatable }
}

})